import type { PageComponentProps } from '@/features/a-dynamic-page/components/page-component-switch';
import type { SpotHeaderV3Attributes } from '@/features/spot-header/spot-header-v3/spot-header-v3-type';

import React from 'react';

import dynamic from 'next/dynamic';

import InfoText from '@/core/components/info-text/info-text';
import { Badges, HtmlTextWrapper, IconImage, ScrollToComponentLink } from '@/core/features';
import useMapOverlayHistoryState from '@/features/map/map-container/use-map-overlay-history';

const SpotScoreOverlay = dynamic(
    () =>
        import(
            /* webpackChunkName: "SpotScoreOverlay" */ '@/features/spot-score/spot-score-overlay/spot-score-overlay'
        ),
);

export type SpotHeaderV3Props = PageComponentProps<SpotHeaderV3Attributes>;

export default function SpotHeaderV3({
    attributes,
    groupComponentProps = { isFullWidth: false, isLastGroupItem: false },
}: SpotHeaderV3Props) {
    const { badges, infoBar, mapButton, scrollToComponentCta, spotScore, subTitle, title } = attributes;
    const { isFullWidth } = groupComponentProps;
    const [isSpotScoreOverlayOpen, setSpotScoreOverlayOpen] = React.useState(false);
    const { openOverlay } = useMapOverlayHistoryState();

    return (
        <>
            <div
                className={'full-width shadow-bottom'}
                data-qa-id={'qa-spot-header'}
            >
                <div
                    className={`flex-space-between-horizontal padding-bottom-16 ${isFullWidth ? 'padding-left-16 padding-right-16' : ''}`}
                >
                    <div className={'flex-column min-width-0 line-height-22 font-size-18 gap-8'}>
                        <div className={'flex-column gap-4'}>
                            <h1 className={'flex-column break-word font-size-18 bold'}>{title}</h1>
                            {subTitle && <h2 className={'justify-start'}>{subTitle}</h2>}
                        </div>
                        {badges.length > 0 && (
                            <div className={'flex-start-center'}>
                                <Badges
                                    badges={badges}
                                    fontSize={14}
                                    type={'horizontal'}
                                    wrap={'wrap'}
                                />
                            </div>
                        )}
                        {(scrollToComponentCta || mapButton) && (
                            <>
                                {scrollToComponentCta && (
                                    <ScrollToComponentLink componentIri={scrollToComponentCta.target}>
                                        <div className={'flex-start-center font-size-16 text-endeavour gap-8 pointer'}>
                                            <IconImage
                                                height={15}
                                                url={scrollToComponentCta.iconUrl}
                                                width={15}
                                            />
                                            <HtmlTextWrapper
                                                htmlText={scrollToComponentCta.htmlText}
                                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                                style={{ lineHeight: '21px' }}
                                            />
                                        </div>
                                    </ScrollToComponentLink>
                                )}
                                {mapButton && (
                                    <button
                                        className={'flex-start-center font-size-16 text-endeavour gap-8 pointer'}
                                        data-qa-id={'qa-spot-header-map-button'}
                                        onClick={() => openOverlay({ trackingElement: 'map_textlink' })}
                                    >
                                        <IconImage
                                            height={12}
                                            url={mapButton.iconUrl}
                                            width={16}
                                        />
                                        <span
                                            style={{
                                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                                lineHeight: '21px',
                                            }}
                                        >
                                            {mapButton.text}
                                        </span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    {spotScore && (
                        <div
                            className={'flex-center-vertical text-center flex shrink-0'}
                            data-qa-id={'qa-spot-header-spot-score'}
                            onClick={() => setSpotScoreOverlayOpen(true)}
                            style={{ paddingTop: !spotScore.htmlScoreDescription ? '13px' : 'unset' }}
                        >
                            <div
                                className={
                                    'flex-center text-white font-size-12 shrink-0 bg-check24Blue bold pointer border-radius-5 align-self-center'
                                }
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                style={{ borderBottomRightRadius: '0', height: '26px', width: '26px' }}
                            >
                                {spotScore.scoreText}
                            </div>
                            {spotScore.htmlScoreDescription && (
                                <div className={'margin-top-5 text-spaces font-size-12 bold inline'}>
                                    <HtmlTextWrapper htmlText={spotScore.htmlScoreDescription} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {infoBar && (
                    <InfoText
                        backgroundColor={infoBar.backgroundColor}
                        fontSize={'12px'}
                        fullWidth={true}
                        icon={infoBar.iconUrl}
                        iconHeight={14}
                        iconWidth={14}
                        padding={'4px 32px'}
                        text={infoBar.htmlText}
                        textColor={infoBar.textColor}
                    />
                )}
            </div>
            {isSpotScoreOverlayOpen && spotScore?.details && (
                <SpotScoreOverlay
                    onClose={() => setSpotScoreOverlayOpen(false)}
                    spotScoreDetails={spotScore.details}
                    spotScoreText={spotScore.scoreText}
                />
            )}
        </>
    );
}
